import { useState, useCallback, useRef } from 'react';
import { fetchGameData } from '../lib/functions';
import type { Filter } from '../types/filter';
import log from '../utils/logger';

export interface GameData {
    status: number;
    menu: MenuItem[];
    categories?: Category[];
    games?: Game[];
    filters?: Filter[];
    suggestions?: Game[];
}

export interface Game {
    id: string;
    name: string;
    gameCode: string;
    launchCode: string;
    type: string;
    subtype: string;
    platform: {
        color: string;
        id: number;
    };
    provider: string;
    external: boolean;
    order: number;
    info?: {
        rtp?: string;
        branded?: string;
        lines?: string;
        volatility?: string;
        reels?: string;
        jackpot?: number;
        jackpotValue?: number;
        functionality?: string[];
        theme?: string[];
        exclusive?: number;
    };
    images?: {
        thumbURL?: string;
        landscapeImgURL?: string;
        otherImgURL?: string;
    };
    partnerID: string;
    isNew: boolean;
    hasPromo?: boolean;
    promoURL?: string;
    docID: string;
}

export interface Category {
    id: string;
    name: string;
    description?: string;
    type: string;
    order: number;
    games: Game[];
}

export interface MenuItem {
    id: string;
    name: string;
    style: string;
    order: number;
    items?: string[];
    games?: Game[];
    state?: {
        enabled: boolean;
    };
}


interface UseGamesState {
    data: GameData | null;
    loading: boolean;
    error: string | null;
    selectedMenuId: string | null;
}

interface UseGamesActions {
    loadData: (menuId?: string | null, gameType?: string, clearData?: boolean, staticData?: boolean) => Promise<GameData | null>;
    setSelectedMenuId: (menuId: string | null) => void;
    clearError: () => void;
    getAllGames: () => Game[];
}

export const useGames = (): UseGamesState & UseGamesActions => {
    const [data, setData] = useState<GameData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedMenuId, setSelectedMenuId] = useState<string | null>(null);

    // Usa useRef per evitare dipendenze circolari
    const initialized = useRef(false);

    const loadData = useCallback(async (menuId: string | null = null, gameType: string = "slot", clearData: boolean = false, staticData: boolean = false): Promise<GameData | null> => {
        setLoading(true);
        setError(null);

        if (clearData) {
            setData(null);
        }

        try {
            const params: { gameType: string; page: number; menu?: string; staticData?: string } = {
                gameType: gameType,
                page: 0,
                staticData: staticData ? "true" : "false"
            };

            if (menuId) {
                params.menu = menuId;
            }

            const result = await fetchGameData(params);
            setData(result as GameData);
            // Solo alla prima chiamata, seleziona il primo menu se non specificato
            if (!initialized.current && !menuId && result && (result as GameData).menu && (result as GameData).menu.length > 0) {
                setSelectedMenuId((result as GameData).menu[0].id);
                initialized.current = true;
            } else {
                //se presente selezioniamo il menu
                if (menuId) {
                    setSelectedMenuId(menuId)
                }
            }
            
            return result as GameData;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Errore nel caricamento dei dati';
            setError(errorMessage);
            log.error('Errore caricamento giochi:', err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const setSelectedMenuIdWrapper = useCallback((menuId: string | null) => {
        setSelectedMenuId(menuId);
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const getAllGames = useCallback((): Game[] => {
        if (!data) return [];
        const allGames: Game[] = [];

        // AGGIUNGI QUESTE RIGHE
        if (data.games) {
            allGames.push(...data.games);
        }

        // Giochi dalle categorie
        if (data.categories) {
            data.categories.forEach(category => {
                if (category.games) {
                    allGames.push(...category.games);
                }
            });
        }

        // Giochi dai menu
        if (data.menu) {
            data.menu.forEach(menu => {
                if (menu.games) {
                    allGames.push(...menu.games);
                }
            });
        }

        // Rimuovi duplicati basati sull'ID
        return allGames.filter((game, index, self) =>
            index === self.findIndex(g => g.id === game.id)
        );
    }, [data]);

    return {
        // State
        data,
        loading,
        error,
        selectedMenuId,

        // Actions
        loadData,
        setSelectedMenuId: setSelectedMenuIdWrapper,
        clearError,
        getAllGames
    };
};